import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";

const NAV_LINKS = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/events", label: "Recent Events" },
    { to: "/book", label: "Book" },
    { to: "/calculator", label: "Calculator" },
    { to: "/services", label: "Services" },
    { to: "/beyond", label: "Beyond" },
];

export default function Nav() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-nav"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-obsidian/85 backdrop-blur-xl border-b border-white/10"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="nav-brand"
                    className="flex items-center gap-3 group"
                >
                    <div className="w-10 h-10 border border-gold/60 flex items-center justify-center text-gold font-display text-base tracking-widest group-hover:bg-gold group-hover:text-obsidian transition-colors duration-300">
                        T·S·G
                    </div>
                    <span className="hidden sm:inline font-display text-white text-lg tracking-wide">
                        The <em className="gold-italic">Spirited</em> Gentleman
                    </span>
                </Link>

                {/* Desktop nav */}
                <nav className="hidden md:flex items-center gap-10">
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `relative text-xs tracking-overline uppercase font-medium transition-colors duration-200 ${
                                    isActive
                                        ? "text-gold"
                                        : "text-white/70 hover:text-white"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                {/* Mobile button */}
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    className="md:hidden text-white p-2"
                    aria-label="Toggle menu"
                    data-testid="mobile-menu-toggle"
                >
                    {open ? <X size={24} weight="thin" /> : <List size={24} weight="thin" />}
                </button>
            </div>

            {/* Mobile menu */}
            {open && (
                <div
                    className="md:hidden bg-obsidian/95 backdrop-blur-xl border-t border-white/10 animate-fade-in"
                    data-testid="mobile-menu"
                >
                    <nav className="flex flex-col px-6 py-6 gap-5">
                        {NAV_LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `text-sm tracking-overline uppercase font-medium ${
                                        isActive ? "text-gold" : "text-white/80"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>
                </div>
            )}
        </header>
    );
}
